import { useAtomValue } from 'jotai';
import { createElement, Fragment, type ReactNode } from 'react';
import { activeImportMarkerAtom } from '@/atoms/import';
import { versionAtom } from '@/atoms/version';
import { projectIdAtom } from '@/atoms/workspace';
import { usePermissions } from '@/hooks/usePermissions';

export class ReadOnlyMutationError extends Error {
    constructor(message = 'Mutation blocked: application is in read-only mode (viewing a version)') {
        super(message);
        this.name = 'ReadOnlyMutationError';
    }
}

const DISABLED_PROPS = Object.freeze({ disabled: true } as const);
const ENABLED_PROPS = Object.freeze({ disabled: false } as const);

function gateProps(isReadOnly: boolean) {
    const guardProps = isReadOnly ? DISABLED_PROPS : ENABLED_PROPS;
    const guard = <T extends (...args: unknown[]) => unknown>(fn: T): T => {
        if (isReadOnly) {
            return ((..._args: unknown[]) => {}) as unknown as T;
        }
        return fn;
    };
    return { guardProps, guard } as const;
}

/**
 * The workspace is FROZEN: a version is being viewed, or this project is
 * being rewritten by an import. Nothing may be written by anyone — members
 * and company info included. The role-agnostic half of {@link useReadOnlyGate};
 * the screens that are not about project content (employees, projects list,
 * company card, versions, export) read this one, so a company admin — who
 * never edits content — still manages members.
 */
export function useVersionGate() {
    const version = useAtomValue(versionAtom);
    const activeImportMarker = useAtomValue(activeImportMarkerAtom);
    const projectId = useAtomValue(projectIdAtom);
    // Only the project the import is actually rewriting goes read-only —
    // importing project A must not lock editing on project B.
    const importInProgress = activeImportMarker !== null && activeImportMarker.project_id === projectId;
    const frozenBy: FrozenBy | null = importInProgress ? 'import' : version !== '' ? 'version' : null;
    const isReadOnly = frozenBy !== null;

    return { isReadOnly, frozenBy, ...gateProps(isReadOnly) } as const;
}

type FrozenBy = 'version' | 'import';
/** Why project content is read-only: the workspace is frozen, or the role
 *  writes no content. */
export type ReadOnlyReason = FrozenBy | 'role';

/**
 * A frozen workspace wins over the role. The role only counts once it is
 * known: while `GET user` / `GET roles` are in flight (or `GET roles` failed)
 * a project owner would otherwise flash read-only — the backend still refuses
 * a write the role doesn't allow.
 */
export function readOnlyReason(input: {
    frozenBy: FrozenBy | null;
    accessResolved: boolean;
    canEditContent: boolean;
}): ReadOnlyReason | null {
    if (input.frozenBy) return input.frozenBy;
    return input.accessResolved && !input.canEditContent ? 'role' : null;
}

/** The editor header's read-only chip — a role read-only must not speak of a
 *  version the user isn't viewing. */
export function readOnlyLabelKey(reason: ReadOnlyReason) {
    return ({ import: 'import-in-progress', version: 'version-read-only', role: 'role-read-only' } as const)[reason];
}

/**
 * Project CONTENT is read-only for the signed-in user: the workspace is frozen
 * ({@link useVersionGate}), or their role in the project is not the author
 * level (#1836 — editors and admins read the script, the plan, the lines, the
 * characters…) — once that role is known ({@link readOnlyReason}). Everything
 * that edits content reads this gate. Notes are the
 * one carve-out and have their own capability (`FountainBlockCapabilities.notes`).
 */
export function useReadOnlyGate() {
    const frozen = useVersionGate();
    const { resolved, canEditContent } = usePermissions();
    const reason = readOnlyReason({ frozenBy: frozen.frozenBy, accessResolved: resolved, canEditContent });
    const isReadOnly = reason !== null;

    return { isReadOnly, reason, ...gateProps(isReadOnly) } as const;
}

export function WriteOnly({ children }: { children: ReactNode }) {
    const { isReadOnly } = useReadOnlyGate();
    if (isReadOnly) return null;
    return createElement(Fragment, null, children);
}
